import { Command, CommandEnum, EditWallPropertiesCommand, EditTechnicalElementCommand } from "./CommandModule";
import { Events } from "../events";
import { eventsManager } from "../managers/EventsManager";
import { EntityFactory,  Room, SceneConstants, roomManager } from "../SavaneJS";

type FunctionnalityChangeCallback = (modifiedRoom: Room, command: EditRoomPropertiesCommand) => void;

export class EditRoomPropertiesCommand extends Command {
    private _modifiedRoom: Room;
    private _currentRoom: Room;
    // To store commands created to perform the change
    public subCommands: Array<Command> = [];
    public functionnalityChangeCallback: FunctionnalityChangeCallback | null = null;
    
    constructor(currentRoom: Room) {
        super();
        // Clone current room so it copies the temporary fields into the cloned room entity
        this._modifiedRoom = EntityFactory.getInstance().cloneEntity(currentRoom, false) as Room;

        this._currentRoom = currentRoom;

        let i: number;
        let roomWalls = currentRoom.walls;
        let nonRoomedWalls = currentRoom.nonRoomedWalls;

        // End the room temporary so it drops the temporary data
        currentRoom.endTemporary();

        // Change room height ?
        if (this._modifiedRoom.height !== this._currentRoom.height) {
            // Parse all walls passed to the constructor and create en edit wall properties command for each of them and store them in the wallcommand field
            for (i = 0; i < roomWalls.length; i++) {
                if (roomWalls[i].height <= 1050 || roomWalls[i].isSpecialWall) {
                    continue;
                }

                let finalHeight = this._modifiedRoom.height;

                for (let j = 0; j < roomWalls[i].rooms.length; j++) {
                    if (roomWalls[i].rooms[j].id !== this._modifiedRoom.id) {
                        if (roomWalls[i].rooms[j].height > finalHeight) {
                            finalHeight = roomWalls[i].rooms[j].height;
                        }
                    }
                }

                roomWalls[i].startTemporary();
                roomWalls[i].height = finalHeight;
                this.subCommands.push(new EditWallPropertiesCommand(roomWalls[i]));
            }
            for (i = 0; i < nonRoomedWalls.length; i++) {
                if (nonRoomedWalls[i].height <= 1050 || nonRoomedWalls[i].isSpecialWall) {
                    continue;
                }
                nonRoomedWalls[i].startTemporary();
                nonRoomedWalls[i].height = this._modifiedRoom.height;
                this.subCommands.push(new EditWallPropertiesCommand(nonRoomedWalls[i]));
            }
        }
        if (this._modifiedRoom.floorHeight !== this._currentRoom.floorHeight) {
            let techElements = currentRoom.floor!.technicalElementsWithStaircases;
            for (i = 0; i < techElements.length; i++) {
                let room = roomManager.getRoomAtPosition(techElements[i].position, currentRoom.floor!);

                if (room) {
                    if (room.id === currentRoom.id) {
                        switch (techElements[i].objectId) {
                            case SceneConstants.TechnicalElementType.beam:
                            case SceneConstants.TechnicalElementType.spotLight:
                            case SceneConstants.TechnicalElementType.frame:
                            case SceneConstants.TechnicalElementType.rosette:
                                techElements[i].startTemporary();
                                techElements[i].floorHeight -= this._modifiedRoom.floorHeight - this._currentRoom.floorHeight;
                                this.subCommands.push(new EditTechnicalElementCommand(techElements[i]));
                                break;
                        }
                    }
                }
            }
        }
    }

    // Command name returned from a global enum
    name(): string {
        return CommandEnum.EditRoomPropertiesCommand;
    }

    execDatas(): any {
        return {
            id: this._currentRoom.id,
            type: this._currentRoom.entityType
        };
    }

    // Echange room parameters between modified and original room entities
    exchangeParameters() {
        let tmpHeight = this._currentRoom.height;
        this._currentRoom.height = this._modifiedRoom.height;
        this._modifiedRoom.height = tmpHeight;

        let tmpFloorHeight = this._currentRoom.floorHeight;
        this._currentRoom.floorHeight = this._modifiedRoom.floorHeight;
        this._modifiedRoom.floorHeight = tmpFloorHeight;

        let tmpPlinths = this._currentRoom.hasPlinths;
        this._currentRoom.hasPlinths = this._modifiedRoom.hasPlinths;
        this._modifiedRoom.hasPlinths = tmpPlinths;

        let tmpPlinthsHeight = this._currentRoom.plinthsHeight;
        this._currentRoom.plinthsHeight = this._modifiedRoom.plinthsHeight;
        this._modifiedRoom.plinthsHeight = tmpPlinthsHeight;

        let tmpPlinthsMaterialType = this._currentRoom.plinthsMaterialType;
        this._currentRoom.plinthsMaterialType = this._modifiedRoom.plinthsMaterialType;
        this._modifiedRoom.plinthsMaterialType = tmpPlinthsMaterialType;

        let tmpCornicesMaterialType = this._currentRoom.cornicesMaterialType;
        this._currentRoom.cornicesMaterialType = this._modifiedRoom.cornicesMaterialType;
        this._modifiedRoom.cornicesMaterialType = tmpCornicesMaterialType;

        let tmpCornicesHeight = this._currentRoom.cornicesHeight;
        this._currentRoom.cornicesHeight = this._modifiedRoom.cornicesHeight;
        this._modifiedRoom.cornicesHeight = tmpCornicesHeight;

        let tmpCornicesDepth = this._currentRoom.cornicesDepth;
        this._currentRoom.cornicesDepth = this._modifiedRoom.cornicesDepth;
        this._modifiedRoom.cornicesDepth = tmpCornicesDepth;
        
        let tmpComponents = this._currentRoom.components;
        this._currentRoom.components = this._modifiedRoom.components;
        this._modifiedRoom.components = tmpComponents;
        
        let tmpCornices = this._currentRoom.hasCornices;
        this._currentRoom.hasCornices = this._modifiedRoom.hasCornices;
        this._modifiedRoom.hasCornices = tmpCornices;

    }

    // Undo the current command
    undo() {
        this.exchangeParameters();

        // Undo all wall properties commands created by the constructor
        for (let i = 0; i < this.subCommands.length; i++) {
            this.subCommands[i].undo();
        }

        eventsManager.instance.dispatch(Events.CHANGE_EDITOR_SELECTION, {
            selection: [this._currentRoom],
            keepSelected: false,
            showTulip: false,
        });

        eventsManager.instance.dispatch(Events.PROJECT_DID_LOAD);
    }

    // Execute current command (redo)
    execute() {
        if (this.functionnalityChangeCallback) {
            this.functionnalityChangeCallback(this._modifiedRoom, this);
        }

        this.exchangeParameters();

        // Execute all wall properties command created by the constrcutor to update all walls
        for (let i = 0; i < this.subCommands.length; i++) {
            this.subCommands[i].execute();
        }

        // Execute parent function
        super.execute();

        eventsManager.instance.dispatch(Events.CHANGE_EDITOR_SELECTION, {
            selection: [this._currentRoom],
            keepSelected: false,
            showTulip: false,
        });

        eventsManager.instance.dispatch(Events.PROJECT_DID_LOAD);
    }
}
